/**
 * Reglas del cobro, sin React.
 *
 * Todo lo que decide plata vive aca y no adentro de un componente: se puede
 * probar exhaustivamente sin montar nada, y es donde alguien va a buscar cuando
 * un saldo no cierre.
 */
import { convertir, type Moneda } from "@tora/shared";

import { TEXTO_MORA, type EstadoMora } from "@/components/clientes/logica";
import type { FilaDeuda } from "@/lib/tipos";

export type { EstadoMora };

export interface CuentaFinanciera {
  id: number;
  nombre: string;
  moneda: Moneda;
  tipo: string;
  activo?: boolean;
}

export interface PagoCobro {
  id: string;
  cuenta_id: number;
  medio: string;
  /**
   * `null` = el campo está vacío o no se entiende. NUNCA se convierte en 0 por
   * defecto: un pago que no parsea bloquea el cobro hasta que se corrija o se
   * quite, en vez de desaparecer en silencio del total.
   */
  importe: number | null;
  /**
   * Los datos del papel, cuando el medio es CHEQUE o ECHEQ.
   *
   * Hasta la auditoria del 24/09/2026 esto no existia y cobrar con cheque era
   * un camino sin salida: la pantalla ofrecia el medio en toda caja de tipo
   * BANCO (ver `mediosDe`) y el backend rechazaba el cobro porque `esquemaPago`
   * exige el objeto `cheque` (clientes.ts:299). El usuario elegia cheque,
   * apretaba y recibia un error que no podia arreglar desde ningun lado.
   */
  cheque?: DatosCheque;
}

/** Lo que hay que anotar de un cheque para que entre a la cartera. */
export interface DatosCheque {
  numero: string;
  banco: string;
  librador: string;
  fecha_cobro: string;
}

/** Si un medio de pago necesita los datos del papel. */
export function esCheque(medio: string): boolean {
  return medio === "CHEQUE" || medio === "ECHEQ";
}

/**
 * Si un pago con cheque esta completo.
 *
 * Los cuatro campos son obligatorios del lado del servidor, asi que pedirlos
 * aca no es rigor de mas: es la diferencia entre avisar mientras se escribe y
 * que el cobro entero rebote al final.
 */
export function chequeCompleto(pago: PagoCobro): boolean {
  if (!esCheque(pago.medio)) return true;
  const c = pago.cheque;
  return (
    c !== undefined &&
    c.numero.trim() !== "" &&
    c.banco.trim() !== "" &&
    c.librador.trim() !== "" &&
    c.fecha_cobro.trim() !== ""
  );
}

/**
 * Un deudor, tal como lo devuelve `GET /deudas`.
 *
 * Se reusa el tipo de `tipos.ts` en vez de redeclararlo: dos definiciones de la
 * misma fila divergen apenas el backend agrega un campo.
 */
export type DeudorFila = FilaDeuda;

// ─────────────────────────────────────────────────────────────────────────────
// Mora
// ─────────────────────────────────────────────────────────────────────────────

/**
 * LA tabla de mora. Los textos y la variante del badge salen de `TEXTO_MORA`
 * (`clientes/logica.ts`), que es la que conoce los cinco estados; aca se le
 * agregan el texto corto (para donde no entra "Debe hace +30 dias") y el orden
 * del listado de deudores. Antes habia tres tablas (esta, la de clientes y una
 * local en `SelectorCliente`) con textos distintos para el mismo cliente.
 *
 * El componente que la dibuja es `BadgeMora` (`cobro/BadgeMora.tsx`). Nadie mas
 * deberia pintar la mora a mano.
 */
export interface Mora {
  texto: string;
  corto: string;
  variante: (typeof TEXTO_MORA)[EstadoMora]["variante"];
  /** Posicion en el listado de deudores: menor = mas urgente. */
  orden: number;
}

export const MORA: Record<EstadoMora, Mora> = {
  MORA_90: { ...TEXTO_MORA.MORA_90, corto: "+90 días", orden: 0 },
  MORA_60: { ...TEXTO_MORA.MORA_60, corto: "+60 días", orden: 1 },
  MORA_30: { ...TEXTO_MORA.MORA_30, corto: "+30 días", orden: 2 },
  AL_DIA: { ...TEXTO_MORA.AL_DIA, corto: "Al día", orden: 3 },
  SIN_DEUDA: { ...TEXTO_MORA.SIN_DEUDA, corto: "Sin deuda", orden: 4 },
};

function esEstadoMora(estado: string): estado is EstadoMora {
  return Object.prototype.hasOwnProperty.call(MORA, estado);
}

/** Un estado desconocido (o ausente) no rompe: cae en AL_DIA. */
export function moraDe(estado: string | null | undefined): Mora {
  return estado && esEstadoMora(estado) ? MORA[estado] : MORA.AL_DIA;
}

/**
 * Orden del listado de deudores: primero los que hace mas tiempo que deben, y
 * dentro de cada tramo los que deben mas.
 *
 * Ordenar solo por importe pondria arriba a un cliente grande que compro ayer y
 * enterraria al que debe poco desde hace cuatro meses — que es justamente al que
 * hay que llamar.
 */
export function ordenarDeudores(filas: DeudorFila[]): DeudorFila[] {
  return [...filas].sort((a, b) => {
    const ma = moraDe(a.estado_mora).orden;
    const mb = moraDe(b.estado_mora).orden;
    if (ma !== mb) return ma - mb;
    return Number(b.saldo_ars_hoy) - Number(a.saldo_ars_hoy);
  });
}

/**
 * Desde cuantos dias una deuda cuenta como "de mas de un mes". Es fijo y no
 * sale de `dias_mora_alerta`: la separacion la pidio el local con esa regla, y
 * si alguien baja la alerta a 15 dias el tramo no tiene que cambiar de nombre.
 */
export const DIAS_DEUDA_VIEJA = 30;

export type TramoDeudor = "VIEJA" | "RECIENTE" | "A_FAVOR";

export interface GrupoDeudores {
  tramo: TramoDeudor;
  titulo: string;
  filas: DeudorFila[];
}

const TITULO_TRAMO: Record<TramoDeudor, string> = {
  VIEJA: "Deben hace más de un mes",
  RECIENTE: "Deben hace menos de un mes",
  A_FAVOR: "Con saldo a favor",
};

/**
 * En que tramo va un deudor. La antiguedad es la del comprobante impago mas
 * viejo (`antiguedad_deuda`), la misma que decide la mora.
 */
export function tramoDe(f: DeudorFila): TramoDeudor {
  if (Number(f.saldo_ars_hoy) < -0.009) return "A_FAVOR";
  return (f.antiguedad_deuda ?? 0) >= DIAS_DEUDA_VIEJA ? "VIEJA" : "RECIENTE";
}

/**
 * Las filas ya ordenadas, partidas en tramos y en ese orden. Solo vuelven los
 * tramos con alguien: un encabezado vacio es ruido en una lista de cobranza.
 */
export function agruparDeudores(filas: ReadonlyArray<DeudorFila>): GrupoDeudores[] {
  const grupos: GrupoDeudores[] = (["VIEJA", "RECIENTE", "A_FAVOR"] as const).map((tramo) => ({
    tramo,
    titulo: TITULO_TRAMO[tramo],
    filas: [],
  }));
  for (const f of filas) grupos.find((g) => g.tramo === tramoDe(f))!.filas.push(f);
  return grupos.filter((g) => g.filas.length > 0);
}

// ─────────────────────────────────────────────────────────────────────────────
// Cobro
// ─────────────────────────────────────────────────────────────────────────────

const redondear = (v: number): number => Math.round(v * 100) / 100;

/**
 * Cuanto se esta cobrando, llevado a la moneda de anclaje del cliente.
 *
 * Cada pago viene en la moneda de SU caja. Compararlos con el saldo exige
 * llevarlos todos a la misma unidad, y la unidad correcta es la del cliente: es
 * la moneda en la que su deuda esta expresada y en la que el FIFO va a imputar.
 */
export function totalCobrado(
  pagos: PagoCobro[],
  cuentas: CuentaFinanciera[],
  monedaAnclaje: Moneda,
  cotizacion: number,
): number {
  return redondear(
    pagos.reduce((acc, p) => {
      const cuenta = cuentas.find((c) => c.id === p.cuenta_id);
      if (!cuenta || p.importe === null) return acc;
      return acc + convertir(p.importe, cuenta.moneda, monedaAnclaje, cotizacion);
    }, 0),
  );
}

export interface ResultadoCobro {
  cobrado: number;
  /** Positivo: queda debiendo. Negativo: pago de mas y queda a favor. */
  resta: number;
  cancelaTodo: boolean;
  quedaAFavor: boolean;
}

export function evaluarCobro(
  saldo: number,
  pagos: PagoCobro[],
  cuentas: CuentaFinanciera[],
  monedaAnclaje: Moneda,
  cotizacion: number,
): ResultadoCobro {
  const cobrado = totalCobrado(pagos, cuentas, monedaAnclaje, cotizacion);
  const resta = redondear(saldo - cobrado);
  // Una deuda en pesos pagada con dolares no llega exacta: el resto de hasta
  // medio centavo de dolar la cierra, como en la base (0121_8). Antes decia
  // «Le sigue quedando $ 4,50» con el importe mas cercano, y esos pesos
  // quedaban para siempre en la cuenta. Una cuenta en dolares ya redondea al
  // centavo: ahi el resto que queda es plata.
  const convierte = pagos.some((p) => {
    const cuenta = cuentas.find((c) => c.id === p.cuenta_id);
    return cuenta !== undefined && p.importe !== null && cuenta.moneda !== monedaAnclaje;
  });
  const tolerancia =
    convierte && monedaAnclaje === "ARS" ? convertir(0.005, "USD", "ARS", cotizacion) + 0.01 : 0.01;
  const cancelaTodo = Math.abs(resta) < tolerancia;
  return {
    cobrado,
    resta,
    cancelaTodo,
    quedaAFavor: resta < -0.009 && !cancelaTodo,
  };
}

/**
 * Como se llama cada medio de pago en pantalla.
 *
 * Existe porque `mediosDe` va para el otro lado: contesta "que medios acepta
 * esta caja" y sirve para ARMAR un cobro. Para LEER uno ya grabado —el panel
 * del historial de ventas tiene el `medio` de `movimientos_caja` y nada mas—
 * hace falta el mapa al reves. Es uno solo para que no se separen.
 */
export const ETIQUETA_MEDIO: Record<string, string> = {
  EFECTIVO_ARS: "Efectivo",
  EFECTIVO_USD: "Dólar billete",
  TRANSFERENCIA: "Transferencia",
  CHEQUE: "Cheque",
  ECHEQ: "Echeq",
  QR_MP: "QR / Mercado Pago",
  DEBITO: "Débito",
  CREDITO: "Crédito",
  OTRO: "Otro",
};

/**
 * El nombre de un medio ya grabado. Si aparece uno que no conocemos se muestra
 * el valor crudo: es feo a proposito, porque un medio sin nombre es un enum
 * nuevo que nadie agrego aca, y esconderlo detras de "Otro" lo haria invisible.
 */
export function etiquetaMedio(medio: string): string {
  return ETIQUETA_MEDIO[medio] ?? medio;
}

/**
 * El nombre de una caja con su moneda adelante del corte: «Efectivo ($)» y
 * «Efectivo (US$)». Las dos cajas de efectivo se llaman igual, y con «(pesos)»
 * y «(dólares)» al final, en el celular el select cortaba en «Efectivo (» y
 * no se distinguia cual era cual.
 */
export function nombreCaja(c: Pick<CuentaFinanciera, "nombre" | "moneda">): string {
  return `${c.nombre} (${c.moneda === "ARS" ? "$" : "US$"})`;
}

/** Medios de pago validos segun el tipo y la moneda de la caja. */
export function mediosDe(cuenta: CuentaFinanciera): Array<{ valor: string; etiqueta: string }> {
  const opciones = (...valores: string[]): Array<{ valor: string; etiqueta: string }> =>
    valores.map((valor) => ({ valor, etiqueta: etiquetaMedio(valor) }));

  if (cuenta.tipo === "CAJA") {
    // En una caja de dolares "efectivo" solo puede ser dolar billete.
    return opciones(cuenta.moneda === "USD" ? "EFECTIVO_USD" : "EFECTIVO_ARS");
  }
  if (cuenta.tipo === "BANCO") {
    return opciones("TRANSFERENCIA", "CHEQUE", "ECHEQ");
  }
  if (cuenta.tipo === "BILLETERA") {
    // Mercado Pago cobra como una transferencia y la plata esta en el momento.
    // Con QR, debito y credito, el cobro iba a «Tarjetas por acreditar» a
    // esperar una acreditacion que nunca se marcaba, y el local no los usa.
    return opciones("TRANSFERENCIA");
  }
  return opciones("OTRO");
}

/**
 * Cuanto proponer en un pago nuevo: lo que falta para cancelar, convertido a la
 * moneda de esa caja.
 *
 * Es el numero que la persona iba a tipear de todas formas. Arrancar en cero
 * obliga a hacer la cuenta a mano, y esa cuenta —con conversion de moneda en el
 * medio— es exactamente donde se equivoca.
 */
export function proponerImporte(
  saldo: number,
  pagosPrevios: PagoCobro[],
  cuentas: CuentaFinanciera[],
  monedaAnclaje: Moneda,
  cuentaDestino: CuentaFinanciera,
  cotizacion: number,
): number {
  // Todo lo anterior en la moneda de esta caja, y distinta de la deuda: el
  // resto se cuenta en esa moneda. Antes pasaba por la moneda de la deuda
  // redondeada a centavos: US$ 56 a 1590 con $ 50.000 ya cargados daba
  // 56 − 31,45 = 24,55 → $ 39.034,50, en vez de los $ 39.040 que faltan.
  const validos = pagosPrevios.filter((p) => p.importe !== null);
  const mismaMoneda =
    cuentaDestino.moneda !== monedaAnclaje &&
    validos.length > 0 &&
    validos.every(
      (p) => cuentas.find((c) => c.id === p.cuenta_id)?.moneda === cuentaDestino.moneda,
    );
  if (mismaMoneda) {
    const previos = validos.reduce((acc, p) => acc + (p.importe ?? 0), 0);
    return Math.max(
      redondear(convertir(saldo, monedaAnclaje, cuentaDestino.moneda, cotizacion) - previos),
      0,
    );
  }
  const ya = totalCobrado(pagosPrevios, cuentas, monedaAnclaje, cotizacion);
  const resta = Math.max(redondear(saldo - ya), 0);
  return redondear(convertir(resta, monedaAnclaje, cuentaDestino.moneda, cotizacion));
}

/**
 * Cuenta por defecto para el primer pago: la caja en pesos, que es como se cobra
 * casi siempre. Si no hay, la primera activa.
 */
export function cuentaPorDefecto(cuentas: CuentaFinanciera[]): CuentaFinanciera | undefined {
  const activas = cuentas.filter((c) => c.activo !== false);
  return activas.find((c) => c.tipo === "CAJA" && c.moneda === "ARS") ?? activas[0];
}

/**
 * De que caja sale el vuelto. Es la misma regla que asienta
 * `emitir_venta_contado` (0121_1): el vuelto NUNCA se da en dolares, sale
 * siempre en pesos del cajon de Efectivo en pesos —el que se toco en el pago,
 * si no el primero activo—, aunque la venta sea en dolares o se pague con
 * dolares. `null` si no hay ninguno: la base frena la venta con vuelto.
 *
 * Antes (0113) salia del ultimo cajon en la moneda de la venta o, si no, del
 * ultimo cajon en la moneda que fuera: una venta de $ 9.000 pagada con US$ 10
 * decia «Sale de Efectivo (US$): US$ 4,34» mientras el cajero daba $ 6.900
 * del cajon de pesos, y los dos arqueos descuadraban.
 */
export function cajaDelVuelto(
  pagos: PagoCobro[],
  cuentas: CuentaFinanciera[],
): CuentaFinanciera | null {
  const esCajonPesos = (c: CuentaFinanciera) =>
    c.tipo === "CAJA" && c.moneda === "ARS" && c.activo !== false;
  const usado = pagos
    .filter((p) => p.importe !== null && p.importe > 0)
    .map((p) => cuentas.find((c) => c.id === p.cuenta_id))
    .filter((c): c is CuentaFinanciera => c !== undefined && esCajonPesos(c))
    .pop();
  const primero = cuentas.filter(esCajonPesos).sort((x, y) => x.id - y.id)[0];
  return usado ?? primero ?? null;
}

/**
 * Cuantos pesos salen de vuelto. Es la misma cuenta que asienta
 * `emitir_venta_contado` (0121_1): los pesos que entraron —cada pago y el
 * equipo en parte de pago, pasados a pesos— menos el total en pesos.
 *
 * Antes se convertia la diferencia en la moneda de la venta, ya redondeada:
 * US$ 10 a 1590 pagados con $ 20.000 son 12,578… dolares → 12,58, vuelto
 * US$ 2,58 × 1590 = $ 4.102,20, cuando el cajero da $ 4.100. En una venta en
 * pesos da lo mismo que antes.
 */
export function vueltoEnPesos(args: {
  pagos: PagoCobro[];
  cuentas: CuentaFinanciera[];
  /** El equipo en parte de pago, si hay. */
  entrega: { valor_toma: number; moneda?: Moneda } | null;
  /**
   * El saldo a favor usado, en la moneda de la venta. Tambien paga: con
   * cliente lo de mas es vuelto igual (0145).
   */
  saldoUsado?: number;
  total: number;
  moneda: Moneda;
  cotizacion: number;
}): number {
  const { pagos, cuentas, entrega, saldoUsado = 0, total, moneda, cotizacion } = args;
  const toma = entrega
    ? convertir(entrega.valor_toma, entrega.moneda ?? moneda, "ARS", cotizacion)
    : 0;
  return redondear(
    totalCobrado(pagos, cuentas, "ARS", cotizacion) +
      toma +
      convertir(saldoUsado, moneda, "ARS", cotizacion) -
      convertir(total, moneda, "ARS", cotizacion),
  );
}
